import { useFormik } from 'formik';
import { validatePhoneNumberLength } from 'libphonenumber-js';
import { MuiTelInput } from 'mui-tel-input';
import { useState } from 'react';
import { CelebrationType, PhoneUpdateStatus } from 'src/lib/enum';

import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material';

import { updatePhoneNumber } from '../actions';
import { countryCodes } from '../lib/countries';
import { useCountryDetection } from '../lib/countryDetection';
import { trackPhoneAddedPostLogin } from '../lib/mixpanel';
import { removeAllWhiteSpacesAndDashes } from '../lib/utils';
import { caloTheme } from '../theme/calo';
import FullScreenCelebration from './FullScreenCelebration';

interface AddPhoneModalProps {
  open: boolean;
  onClose: () => void;
  onPhoneAdded: (phoneNumber?: string) => void;
  affiliateId: string;
  initialPhoneNumber?: string;
}

interface AddPhoneValues {
  phoneNumber: string;
}

const AddPhoneModal = ({ open, onClose, onPhoneAdded, affiliateId, initialPhoneNumber }: AddPhoneModalProps) => {
  const { detectedCountry } = useCountryDetection();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState<string>('');

  const isUpdateMode = Boolean(initialPhoneNumber);

  const form = useFormik<AddPhoneValues>({
    initialValues: {
      phoneNumber: initialPhoneNumber || ''
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: any = {};
      if (!values.phoneNumber) {
        errors.phoneNumber = 'Phone number is required';
      } else if (validatePhoneNumberLength(values.phoneNumber)) {
        errors.phoneNumber = 'Please enter a valid phone number';
      }
      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      setError(null);

      try {
        await updatePhoneNumber(values.phoneNumber);

        trackPhoneAddedPostLogin(affiliateId, values.phoneNumber, PhoneUpdateStatus.IN_PROGRESS);
        setUpdatedPhoneNumber(values.phoneNumber);
        setShowCelebration(true);
        onPhoneAdded(values.phoneNumber);
      } catch (error: any) {
        switch (error.response?.status) {
          case 404: {
            setError('Cannot find the requested affiliate');
            break;
          }
          case 400: {
            setError('Invalid phone number format');
            break;
          }
          case 409: {
            setError('Phone number already exists. Please contact support.');
            break;
          }
          case 500: {
            setError('Failed to update phone number. Please contact support.');
            break;
          }
          default: {
            setError('Failed to update phone number. Please contact support.');
            break;
          }
        }
      } finally {
        setLoading(false);
      }
    }
  });

  const handleClose = () => {
    form.resetForm();
    setError(null);
    onClose();
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isUpdateMode ? 'Update Phone Number' : 'Add Phone Number'}
          </Typography>
        </DialogTitle>

        <form onSubmit={form.handleSubmit}>
          <DialogContent sx={{ pb: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {isUpdateMode
                ? 'Please update your phone number so you can use it to log in more easily in the future.'
                : 'Please add your phone number so you can use it to log in more easily in the future.'}
            </Typography>

            <MuiTelInput
              margin="normal"
              required
              fullWidth
              id="phoneNumber"
              label="Phone Number"
              name="phoneNumber"
              value={form.values.phoneNumber}
              onChange={(e) => {
                form.setFieldValue('phoneNumber', removeAllWhiteSpacesAndDashes(e));
              }}
              onBlur={form.handleBlur('phoneNumber')}
              error={Boolean(form.errors.phoneNumber && form.touched.phoneNumber)}
              helperText={form.errors.phoneNumber && form.touched.phoneNumber ? form.errors.phoneNumber : ''}
              autoFocus
              onlyCountries={countryCodes}
              defaultCountry={detectedCountry}
            />

            {error && (
              <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !form.isValid}
              sx={{
                textTransform: 'none',
                color: caloTheme.palette.white
              }}
            >
              {loading ? (isUpdateMode ? 'Updating...' : 'Adding...') : (isUpdateMode ? 'Update Phone Number' : 'Add Phone Number')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {showCelebration && (
        <FullScreenCelebration
          type={CelebrationType.PHONE_UPDATE}
          onClose={handleCelebrationClose}
          phoneNumber={updatedPhoneNumber}
        />
      )}
    </>
  );
};

export default AddPhoneModal; 